'use client';

import { useMicrocopy } from '@contexts/MicrocopyContext';
import { MicrocopyItem } from '@lib/types/cms/MicrocopySet';
import { getMicrocopyValue } from '@lib/util/microcopy';
import { Faders } from 'phosphor-icons';
import { useCallback, useEffect, useState } from 'react';
import { c, useBreakpoints } from 'ui-components';
import { MobileFilterDrawer } from '../MobileFilterDrawer';
import { MobileFilterBarProps } from './MobileFilterBar.types';

/**
 * Mobile filter entry point for the SSR search path: the "Sort and Filter"
 * trigger and the full-screen drawer it opens.
 *
 * The active-filter chips sit just below in SearchSsrLayout, as the single
 * `FacetBreadcrumbs` instance both surfaces share — so removing a filter from
 * a chip behaves identically on mobile and desktop.
 */
export const MobileFilterBar = ({ className }: MobileFilterBarProps) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { searchLabelsMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyItem[];
  }>();

  const closeDrawer = useCallback(() => setIsDrawerOpen(false), []);

  // This whole bar is `lg:hidden`, so crossing into the desktop breakpoint
  // would leave an open drawer display:none while its scroll lock and focus
  // trap stayed installed over the desktop UI. `isDesktop` is the same
  // 1024px query the class compiles to.
  const isDesktop = useBreakpoints()?.isDesktop;
  useEffect(() => {
    if (isDesktop) closeDrawer();
  }, [isDesktop, closeDrawer]);

  return (
    <div className={c('flex flex-col gap-3', className)}>
      <button
        type="button"
        onClick={() => setIsDrawerOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={isDrawerOpen}
        data-test="mobile-sort-and-filter-button"
        className="flex w-full items-center justify-center gap-2 rounded-full border-2 border-grey-600 py-2.5 text-sm font-semibold uppercase tracking-wider text-grey-800"
      >
        <Faders size={16} />
        {getMicrocopyValue(
          searchLabelsMicrocopy,
          'search.sort-and-filter',
          'Sort and Filter'
        )}
      </button>

      {/* Mounted only while open: the drawer subscribes to every facet, the
          breadcrumb manager, the query summary, sort and page size, and this
          bar renders on desktop too (hidden by `lg:hidden`), so a permanently
          mounted drawer would recount an invisible publisher value set on
          every search response of every viewport. */}
      {isDrawerOpen && (
        <MobileFilterDrawer isOpen={isDrawerOpen} onClose={closeDrawer} />
      )}
    </div>
  );
};
